import { useMemo } from "react";
import PropTypes from "prop-types";

import { alignItems, colors, strCode } from "../../../../../../consts";
import { t } from "../../../../../../services";
import { dateUtil } from "../../../../../../utils";

import {
  Caption,
  FlexContainers,
  Paragraph2,
  SVG,
  TextButton,
  WrapperContainer
} from "../../../../../../components";

export const CampaignInfo = ({
  notificationCampaignData,
  loading,
  onDeactivate,
  settingsAvailable
}) => {
  const apps = useMemo(() => {
    return notificationCampaignData?.audience?.appTypes?.join(", ") || "";
  }, [notificationCampaignData]);

  const devices = useMemo(() => {
    return notificationCampaignData?.audience?.deviceTypes?.join(", ") || "";
  }, [notificationCampaignData]);

  const createdDate = useMemo(() => {
    if (notificationCampaignData?.createdDate) {
      return dateUtil.formatDateFromDayFirstFormat(notificationCampaignData.createdDate).getTime();
    }

    return "";
  }, [notificationCampaignData]);

  const startDateTs = useMemo(() => {
    return dateUtil.getUTCDate(new Date(notificationCampaignData?.startTs))?.getTime();
  }, [notificationCampaignData]);

  const onDeactivateCampaign = () => {
    onDeactivate();
  };

  return (
    <WrapperContainer
      Container={FlexContainers.FlexColumnBetween}
      alignItems={alignItems.start}
      width={"100%"}>
      <WrapperContainer Container={FlexContainers.FlexColumnStart} gap={16}>
        {apps && (
          <WrapperContainer
            Container={FlexContainers.FlexColumnStart}
            alignItems="start"
            width="100%">
            <Caption>{t(strCode.APPLICATIONS_LABEL).toUpperCase()}</Caption>
            <Paragraph2>{apps}</Paragraph2>
          </WrapperContainer>
        )}

        {devices && (
          <WrapperContainer
            Container={FlexContainers.FlexColumnStart}
            alignItems="start"
            width="100%">
            <Caption>{t(strCode.DEVICES_LABEL).toUpperCase()}</Caption>
            <Paragraph2>{devices}</Paragraph2>
          </WrapperContainer>
        )}

        <WrapperContainer
          Container={FlexContainers.FlexColumnStart}
          width="100%"
          alignItems="start">
          <Caption>{t(strCode.START_DATE_TITLE).toUpperCase()}</Caption>

          <WrapperContainer Container={FlexContainers.FlexRowStart} gap={8} width={"100%"}>
            <SVG.CalendarIconNew />
            <Paragraph2>{dateUtil.formatDateFromTimestamp(startDateTs || "")}</Paragraph2>
          </WrapperContainer>
        </WrapperContainer>

        {notificationCampaignData?.createdBy && (
          <WrapperContainer
            Container={FlexContainers.FlexColumnStart}
            width="100%"
            alignItems="start">
            <Caption>{t(strCode.OWNER_TITLE).toUpperCase()}</Caption>

            <WrapperContainer Container={FlexContainers.FlexRowStart} gap={8} width={"100%"}>
              <Paragraph2>{`${notificationCampaignData.createdBy.first_name} ${notificationCampaignData.createdBy.last_name}`}</Paragraph2>
            </WrapperContainer>
          </WrapperContainer>
        )}

        {createdDate && (
          <WrapperContainer
            Container={FlexContainers.FlexColumnStart}
            width="100%"
            alignItems="start">
            <Caption>{t(strCode.CREATED_DATE_TITLE).toUpperCase()}</Caption>

            <WrapperContainer Container={FlexContainers.FlexRowStart} gap={8} width={"100%"}>
              <SVG.CalendarIconNew />
              <Paragraph2>{dateUtil.formatDateFromTimestamp(createdDate)}</Paragraph2>
            </WrapperContainer>
          </WrapperContainer>
        )}
      </WrapperContainer>

      {settingsAvailable && (
        <WrapperContainer>
          <TextButton color={colors.ERROR} disabled={loading} onClick={onDeactivateCampaign}>
            {t(strCode.DEACTIVATE_BTN)}
          </TextButton>
        </WrapperContainer>
      )}
    </WrapperContainer>
  );
};

CampaignInfo.propTypes = {
  notificationCampaignData: PropTypes.shape({
    campaignName: PropTypes.string.isRequired,
    markets: PropTypes.arrayOf(PropTypes.string).isRequired,
    startTs: PropTypes.string.isRequired,
    endTs: PropTypes.string,
    status: PropTypes.string.isRequired,
    audience: PropTypes.shape({
      deviceTypes: PropTypes.arrayOf(PropTypes.string),
      appTypes: PropTypes.arrayOf(PropTypes.string),
      firmwareVersions: PropTypes.arrayOf(PropTypes.string)
    }).isRequired,
    createdBy: PropTypes.shape({
      first_name: PropTypes.string.isRequired,
      last_name: PropTypes.string.isRequired
    }),
    createdDate: PropTypes.string
  }),
  loading: PropTypes.bool.isRequired,
  onDeactivate: PropTypes.func.isRequired,
  settingsAvailable: PropTypes.bool.isRequired
};
